export const SITE_URL = 'https://forteunoweb.com'

export const CASABLANCA_CASE_PATH = '/case-studies/clinique-dentaire-casablanca-agent-ia-whatsapp'
export const DARIJA_DEMO_PATH = '/projects/demo-agent-ia-whatsapp-darija'
export { LEAD_ENRICHMENT_PATH, DENTAL_LANDING_PATH, LAW_FIRM_SITE_PATH, TRANSLATION_PLATFORM_PATH } from './projectDemos'
export const WHATSAPP_RESOURCE_PATH = '/resources/whatsapp-bloque-24-heures-api-officielle'
export const DARIJA_RESOURCE_PATH = '/resources/ai-chatbot-moroccan-darija-dialects'
export const SOP_RESOURCE_PATH = '/resources/pourquoi-agent-ia-medical-a-besoin-sops'

export const ORGANIZATION_ID = `${SITE_URL}/#organization`
export const AUTHOR_ID = `${SITE_URL}/about/mohamed-amine-saada#person`

// Verified at https://fast.wistia.com/embed/medias/na1pc11zly.json
// on 2026-10-10 (createdAt=1783108187, duration=169.204). Keep the
// timestamp tied to this media ID; an environment override is a different
// video and must not inherit this video's date or duration.
const VERIFIED_WISTIA_METADATA: Record<string, { uploadDate: string; duration: string }> = {
  na1pc11zly: { uploadDate: '2026-07-03T19:49:47Z', duration: 'PT169.204S' },
}

/**
 * VideoObject schema for the shared Wistia-hosted VSL (mediaId `na1pc11zly`,
 * reused on /dentaire and /doctors-agent + /doctors-agent-2). Without this,
 * Google had no way to associate the embedded video with these pages at all
 * (the wistia-player element only mounts client-side after a click, so it
 * never appears in the prerendered HTML crawlers see).
 */
export function wistiaVideoSchema(opts: {
  pageUrl: string
  mediaId: string
  name: string
  description: string
  thumbnailUrl: string
  uploadDate: string
  inLanguage: string
}) {
  const verifiedMetadata = VERIFIED_WISTIA_METADATA[opts.mediaId]
  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    '@id': `${opts.pageUrl}#video`,
    name: opts.name,
    description: opts.description,
    thumbnailUrl: opts.thumbnailUrl,
    embedUrl: `https://fast.wistia.net/embed/iframe/${opts.mediaId}`,
    uploadDate: verifiedMetadata?.uploadDate ?? opts.uploadDate,
    ...(verifiedMetadata ? { duration: verifiedMetadata.duration } : {}),
    inLanguage: opts.inLanguage,
    isFamilyFriendly: true,
  }
}

/**
 * Only the 6 flagship cities (real per-city copy in `moroccoLocations.ts`,
 * 2 unique FAQ items each in `moroccoSeo.ts`) are indexed. The 8 "addition"
 * cities in `moroccoLocationAdditions.ts` get only 1 unique FAQ item each and
 * share ~70-75% boilerplate with every other city page — noindexed until each
 * gets a second unique FAQ item and a real differentiation check (see
 * forteunoweb.com-audit/findings/programmatic.md C1/C2).
 */
export const INDEXED_CITY_SLUGS = [
  'casablanca',
  'rabat',
  'marrakech',
  'tanger',
  'fes',
  'agadir',
] as const

export const CITY_NEIGHBORS: Record<string, readonly string[]> = {
  casablanca: ['rabat', 'kenitra', 'el-jadida'],
  rabat: ['sale', 'kenitra', 'casablanca'],
  marrakech: ['safi', 'agadir', 'casablanca'],
  fes: ['meknes', 'oujda', 'nador'],
  agadir: ['marrakech', 'safi'],
  tanger: ['tetouan', 'rabat'],
  meknes: ['fes', 'rabat'],
  oujda: ['nador', 'fes'],
  kenitra: ['rabat', 'sale', 'casablanca'],
  sale: ['rabat', 'kenitra'],
  tetouan: ['tanger', 'rabat'],
  'el-jadida': ['casablanca', 'safi'],
  safi: ['marrakech', 'agadir'],
  nador: ['oujda', 'fes'],
}

export function isIndexedCity(slug: string) {
  return (INDEXED_CITY_SLUGS as readonly string[]).includes(slug)
}
